import { useEffect, useMemo, useState } from 'react';
import { api } from '../../../core/utils/apiClient';
import { authService } from '../../auth/services/auth.service';
import { Company, Document } from '../../dashboard_user/types';
import { CompanyFormState, ProgressStep } from '../components';
import { CompanySource } from '../companySources';
import {
  CATEGORY_OPTIONS,
  FlowStep,
  INITIAL_FORM_STATE,
  areRequiredFieldsFilled,
  formatMissingFieldsMessage,
  getMissingRequiredFields,
  mapCompanyToForm,
} from '../constants';
import { enrichmentService } from '../services/enrichment.service';
import {
  EnrichmentField,
  EnrichmentForm,
  EnrichmentSession,
  FieldSubmissionResult,
} from '../types';

export type EnrichmentMode = 'partner' | 'admin';

export interface ExistingSessionConflict {
  sessionId: string;
  session: EnrichmentSession | null;
  loading: boolean;
}

export interface EnrichmentFlowFeatures {
  /**
   * Write `localStorage.enrichmentStatus = 'completed'` and refresh user info
   * after a successful finalize / skip-to-completion.
   *
   * Partner: true (gates the `ProtectedRoute` enrichment-status check).
   * Admin: false (admin's auth state must not be mutated when enriching
   * someone else's company).
   */
  markEnrichmentCompleteOnFinalize: boolean;
  /**
   * Write `localStorage.enrichmentStatus = 'completed'` BEFORE the skip API call.
   * Prevents a router race where `ProtectedRoute` bounces the partner back to
   * `/enrichment` mid-skip.
   *
   * Partner: true. Admin: false.
   */
  writeLocalEnrichmentStatusOnSkip: boolean;
  /**
   * When the partner skips (from conflict modal OR mid-flow), immediately mark
   * complete and call `onAfterSkipToCompletion` (which typically navigates to
   * dashboard). When the admin skips, instead park at the `'finalization'` step
   * so the admin can review before clicking Finalize.
   *
   * Partner: true. Admin: false.
   */
  completeAndExitOnSkip: boolean;
  /**
   * After loading a company that's already been enriched (`enrichment_score > 0`),
   * land on the `'overview'` flow step instead of `'upload'`.
   *
   * Partner: true (shows the partner a "your enrichment is complete" view).
   * Admin: false (admin always re-enters at upload to re-enrich).
   */
  enableOverviewStep: boolean;
}

export interface UseEnrichmentFlowOptions {
  /**
   * Informational only — for JSX branching in consumers if needed.
   * NEVER used to gate authorization. Authz lives at the route boundary
   * (`AdminRoute` / `ProtectedRoute`) and on the backend (`verify_role_access`).
   */
  mode: EnrichmentMode;
  companySource: CompanySource;
  features: EnrichmentFlowFeatures;

  /**
   * Called after a successful finalize. The consumer typically navigates here.
   * Partner: navigate to `/dashboard` (or `window.location.href` for a hard redirect).
   * Admin: navigate to `/admin/companies/:id`.
   */
  onAfterFinalize: (ctx: {
    session: EnrichmentSession | null;
    company: Company | null;
  }) => void | Promise<void>;
  /**
   * Called after a successful skip-to-completion flow. Same shape as `onAfterFinalize`
   * but for the skip path. Only called when `features.completeAndExitOnSkip === true`.
   */
  onAfterSkipToCompletion: (ctx: {
    sessionId: string | null;
  }) => void | Promise<void>;
  /**
   * Optional callback fired after `markEnrichmentComplete` runs, so the consumer
   * can refresh React-side auth state (e.g. `useAuth().refreshUser()`).
   */
  onAuthStateMayHaveChanged?: () => void | Promise<void>;
  /**
   * Returns the form state to use when the company source loads "fresh" (no
   * company yet). Defaults to `INITIAL_FORM_STATE`.
   *
   * Partner uses this to seed the form with `location.state.prefilledCompanyName`.
   */
  getFreshFormState?: () => CompanyFormState;
}

export interface UseEnrichmentFlowReturn {
  // ── State ──
  flowStep: FlowStep;
  company: Company | null;
  companyForm: CompanyFormState;
  companyLoading: boolean;
  companySaved: boolean;
  companyError: string | null;
  savingCompany: boolean;

  existingDocuments: Document[];
  selectedExistingDocumentIds: number[];
  initialDocumentIds: number[];
  loadingDocuments: boolean;

  session: EnrichmentSession | null;
  sessionError: string | null;
  startingSession: boolean;
  existingSessionConflict: ExistingSessionConflict | null;

  formData: EnrichmentForm | null;
  fieldResponses: Record<string, string>;
  additionalDocumentIds: number[];
  submittingResponses: boolean;
  responseMessage: string | null;

  finalizing: boolean;
  finalizeMessage: string | null;

  processingMessage: string;
  checkingStatus: boolean;
  loadingForm: boolean;
  enrichmentCompleted: boolean;
  processingAfterSubmit: boolean;

  // ── Derived ──
  readyToStart: boolean;
  progressSteps: ProgressStep[];

  // ── Handlers ──
  setFlowStep: (s: FlowStep) => void;
  handleCompanyInputChange: (field: keyof CompanyFormState, value: string) => void;
  handleSectorChange: (value: number | null) => void;
  handleSaveCompany: () => Promise<void>;
  handleToggleExistingDocument: (id: number) => void;
  handleInitialUploadComplete: (id: number) => void;
  handleStartEnrichment: () => Promise<void>;
  handleViewExistingSession: () => void;
  handleSkipExistingSession: () => Promise<void>;
  handleCancelExistingSession: () => Promise<void>;
  handleViewSessionWhileWaiting: () => Promise<void>;
  handleLoadForm: () => Promise<void>;
  handleSkipEnrichment: () => Promise<void>;
  handleCancelFromProcessing: () => Promise<void>;
  handleFieldChange: (field: EnrichmentField, value: string) => void;
  handleAdditionalUploadComplete: (id: number) => void;
  handleSubmitResponses: () => Promise<void>;
  handleFinalize: () => Promise<void>;
  handleSkipToDashboardFromProcessing: () => Promise<void>;
  closeConflictModal: () => void;
}

const SELECTED_CATEGORIES = CATEGORY_OPTIONS.map((option) => option.value);

export function useEnrichmentFlow(opts: UseEnrichmentFlowOptions): UseEnrichmentFlowReturn {
  const { companySource, features } = opts;

  // ── Company state ──
  const [flowStep, setFlowStep] = useState<FlowStep>('onboarding');
  const [company, setCompany] = useState<Company | null>(null);
  const [companyForm, setCompanyForm] = useState<CompanyFormState>(INITIAL_FORM_STATE);
  const [companyLoading, setCompanyLoading] = useState(true);
  const [companySaved, setCompanySaved] = useState(false);
  const [companyError, setCompanyError] = useState<string | null>(null);
  const [savingCompany, setSavingCompany] = useState(false);

  // ── Document state ──
  const [initialDocumentIds, setInitialDocumentIds] = useState<number[]>([]);
  const [existingDocuments, setExistingDocuments] = useState<Document[]>([]);
  const [selectedExistingDocumentIds, setSelectedExistingDocumentIds] = useState<number[]>([]);
  const [loadingDocuments, setLoadingDocuments] = useState(false);

  // ── Session state ──
  const [session, setSession] = useState<EnrichmentSession | null>(null);
  const [sessionError, setSessionError] = useState<string | null>(null);
  const [startingSession, setStartingSession] = useState(false);
  const [existingSessionConflict, setExistingSessionConflict] = useState<ExistingSessionConflict | null>(null);

  // ── Form state ──
  const [formData, setFormData] = useState<EnrichmentForm | null>(null);
  const [fieldResponses, setFieldResponses] = useState<Record<string, string>>({});
  const [additionalDocumentIds, setAdditionalDocumentIds] = useState<number[]>([]);
  const [submittingResponses, setSubmittingResponses] = useState(false);
  const [responseMessage, setResponseMessage] = useState<string | null>(null);

  const [finalizing, setFinalizing] = useState(false);
  const [finalizeMessage, setFinalizeMessage] = useState<string | null>(null);

  // ── Processing state ──
  const [processingMessage, setProcessingMessage] = useState(
    'We are analyzing your company data and uploaded documents.',
  );
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [loadingForm, setLoadingForm] = useState(false);
  const [enrichmentCompleted, setEnrichmentCompleted] = useState(false);
  const [processingAfterSubmit, setProcessingAfterSubmit] = useState(false);

  // ── Load company on mount (and when companySource changes) ──
  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setCompanyLoading(true);
      setCompanyError(null);

      try {
        const { company: loadedCompany, isFresh } = await companySource.load();
        if (cancelled) return;

        if (loadedCompany && !isFresh) {
          setCompany(loadedCompany);
          const form = mapCompanyToForm(loadedCompany);
          setCompanyForm(form);
          const allFieldsFilled = areRequiredFieldsFilled(form);
          setCompanySaved(allFieldsFilled && Boolean(loadedCompany.id));

          if (allFieldsFilled && loadedCompany.id) {
            const alreadyEnriched =
              loadedCompany.enrichment_score && loadedCompany.enrichment_score > 0;
            if (alreadyEnriched && features.enableOverviewStep) {
              setFlowStep('overview');
            } else {
              setFlowStep('upload');
            }
          } else {
            setFlowStep('onboarding');
          }
        } else {
          // Fresh start — no company yet
          setCompany(null);
          setCompanyForm(opts.getFreshFormState?.() ?? INITIAL_FORM_STATE);
          setCompanySaved(false);
          setFlowStep('onboarding');
        }
      } catch (error: any) {
        if (cancelled) return;
        console.error('Failed to load company:', error);
        setCompanyError(error.message || 'Unable to load company information.');
        setCompany(null);
        setCompanyForm(opts.getFreshFormState?.() ?? INITIAL_FORM_STATE);
        setCompanySaved(false);
        setFlowStep('onboarding');
      } finally {
        if (!cancelled) setCompanyLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
    // We intentionally re-run on companySource identity change.
    // `getFreshFormState` and `features.enableOverviewStep` are read at call-time
    // but not as deps — they don't conceptually trigger a reload.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companySource]);

  // ── Load existing documents when entering the upload step ──
  useEffect(() => {
    if (!company?.id || flowStep !== 'upload') return;

    let cancelled = false;
    const loadDocs = async () => {
      setLoadingDocuments(true);
      try {
        const response = await api.get(`/api/v1/documents/companies/${company.id}/documents`);
        if (response.ok) {
          const data = await response.json();
          const docs: Document[] = data.documents || [];
          if (cancelled) return;
          setExistingDocuments(docs);
          setSelectedExistingDocumentIds(docs.map((d) => d.id));
        }
      } catch (error) {
        console.error('Failed to load existing documents:', error);
      } finally {
        if (!cancelled) setLoadingDocuments(false);
      }
    };

    loadDocs();

    return () => {
      cancelled = true;
    };
  }, [company?.id, flowStep]);

  // ── Internal helper: write completed status to localStorage + refresh user ──
  const markEnrichmentComplete = async () => {
    try {
      if (typeof window !== 'undefined') {
        localStorage.setItem('enrichmentStatus', 'completed');
        await authService.fetchAndStoreUserInfo();
      }
    } catch (error) {
      console.error('Failed to mark enrichment complete:', error);
    }
  };

  // ── Internal helper: decide finalize vs back-to-form after re-scoring ──
  const handlePostRescoringResult = async (readySession: EnrichmentSession) => {
    if (readySession.can_finalize) {
      try {
        await enrichmentService.finalizeSession(readySession.session_id);
        if (features.markEnrichmentCompleteOnFinalize) {
          await markEnrichmentComplete();
        }
        if (opts.onAuthStateMayHaveChanged) {
          await opts.onAuthStateMayHaveChanged();
        }
        setEnrichmentCompleted(true);
        setProcessingMessage('Your enrichment session has been finalised successfully!');
        setTimeout(() => {
          void opts.onAfterFinalize({ session: readySession, company });
        }, 1500);
      } catch (error: any) {
        console.error('Failed to finalise session:', error);
        setSessionError(error.message || 'Failed to finalise session. Please try again.');
        setProcessingAfterSubmit(false);
        setFlowStep('form');
      }
    } else {
      // Score below threshold and tries remaining — reload the updated form
      setProcessingMessage('Re-scoring complete. Loading updated form...');
      try {
        const updatedForm = await enrichmentService.getForm(readySession.session_id);
        setFormData(updatedForm);
        const refreshed: Record<string, string> = {};
        updatedForm.sections.forEach((section) => {
          section.fields.forEach((field) => {
            if (field.current_value) refreshed[field.field_id] = field.current_value;
          });
        });
        setFieldResponses(refreshed);
      } catch (e: any) {
        console.error('Failed to reload form after re-scoring:', e);
      }
      setProcessingAfterSubmit(false);
      setFlowStep('form');
    }
  };

  // ── Handlers ──
  const handleCompanyInputChange = (field: keyof CompanyFormState, value: string) => {
    setCompanyForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSectorChange = (value: number | null) => {
    setCompanyForm((prev) => ({ ...prev, sector_id: value }));
  };

  const handleSaveCompany = async () => {
    setSavingCompany(true);
    setCompanyError(null);

    const missingFields = getMissingRequiredFields(companyForm);
    if (missingFields.length > 0) {
      setCompanyError(formatMissingFieldsMessage(missingFields));
      setSavingCompany(false);
      return;
    }

    try {
      const savedCompany = await companySource.save(companyForm, company);
      setCompany(savedCompany);
      setCompanySaved(true);
      setFlowStep('upload');
    } catch (error: any) {
      console.error('Failed to save company:', error);
      setCompanyError(error.message || 'Unable to save company.');
    } finally {
      setSavingCompany(false);
    }
  };

  const handleToggleExistingDocument = (documentId: number) => {
    setSelectedExistingDocumentIds((prev) =>
      prev.includes(documentId) ? prev.filter((id) => id !== documentId) : [...prev, documentId],
    );
  };

  const handleInitialUploadComplete = (documentId: number) => {
    setInitialDocumentIds((prev) => [...prev, documentId]);
  };

  const readyToStart = useMemo(() => {
    const hasDocuments = selectedExistingDocumentIds.length > 0 || initialDocumentIds.length > 0;
    return !!company && hasDocuments && SELECTED_CATEGORIES.length > 0 && !startingSession;
  }, [company, selectedExistingDocumentIds, initialDocumentIds, startingSession]);

  const handleStartEnrichment = async () => {
    if (!company || !readyToStart) return;

    setStartingSession(true);
    setSessionError(null);
    setExistingSessionConflict(null);

    setFlowStep('processing');
    setEnrichmentCompleted(false);
    setProcessingMessage('Your enrichment session has started.');
    setProcessingAfterSubmit(false);

    try {
      const documentIdsToUse = [...selectedExistingDocumentIds, ...initialDocumentIds];

      if (documentIdsToUse.length === 0) {
        setSessionError('Please upload at least one document before starting enrichment.');
        setFlowStep('upload');
        return;
      }

      const enrichmentSession = await enrichmentService.startSession(
        company.id,
        SELECTED_CATEGORIES,
      );

      if (enrichmentSession.status === 'processing') {
        setProcessingMessage('Analyzing documents and scoring fields...');
        const readySession = await enrichmentService.pollSessionUntilReady(
          enrichmentSession.session_id,
          3000,
          120,
          (polledSession) => {
            setSession(polledSession);
            setProcessingMessage('Still processing — analyzing documents and scoring fields...');
          },
        );

        if (readySession.status === 'failed') {
          throw new Error('Background enrichment processing failed. Please try again.');
        }

        setSession(readySession);
      } else {
        setSession(enrichmentSession);
      }

      setEnrichmentCompleted(true);
      setProcessingMessage('Your enrichment session has completed successfully!');
    } catch (error: any) {
      if (error.message === 'SESSION_EXPIRED') {
        setSessionError('Your session has expired. Please log in again to continue.');
        setFlowStep('upload');
        return;
      }

      if (error.status === 409) {
        // Go back to upload so the conflict modal (rendered inside UploadStep) is visible.
        // Without this, flowStep stays on 'processing' and the modal never renders.
        setFlowStep('upload');

        const sessionId = error.session_id || error.sessionId || 'unknown';
        setExistingSessionConflict({
          sessionId,
          session: null,
          loading: true,
        });

        if (sessionId && sessionId !== 'unknown') {
          try {
            const existingSession = await enrichmentService.getSession(sessionId);
            setExistingSessionConflict({
              sessionId,
              session: existingSession,
              loading: false,
            });
          } catch {
            setExistingSessionConflict({
              sessionId,
              session: null,
              loading: false,
            });
          }
        } else {
          setExistingSessionConflict({
            sessionId: 'unknown',
            session: null,
            loading: false,
          });
        }
        return;
      }

      setSessionError(error.message || 'Failed to start enrichment session.');
      setFlowStep('upload');
    } finally {
      setStartingSession(false);
    }
  };

  const handleViewExistingSession = () => {
    if (!existingSessionConflict?.session) return;
    const existing = existingSessionConflict.session;
    setSession(existing);
    setExistingSessionConflict(null);
    setFlowStep('processing');
    // Only mark complete if the session actually finished. A still-processing session
    // would otherwise render "complete" while Celery is mid-flight.
    setEnrichmentCompleted(existing.status !== 'processing');
  };

  const handleSkipExistingSession = async () => {
    if (!existingSessionConflict?.sessionId || existingSessionConflict.sessionId === 'unknown')
      return;

    setStartingSession(true);
    try {
      if (features.completeAndExitOnSkip) {
        // Partner: full finalize + redirect path
        if (features.writeLocalEnrichmentStatusOnSkip && typeof window !== 'undefined') {
          localStorage.setItem('enrichmentStatus', 'completed');
        }
        await enrichmentService.skipSession(existingSessionConflict.sessionId);
        await enrichmentService.finalizeSession(existingSessionConflict.sessionId);
        if (features.markEnrichmentCompleteOnFinalize) {
          await markEnrichmentComplete();
        }
        await opts.onAfterSkipToCompletion({ sessionId: existingSessionConflict.sessionId });
      } else {
        // Admin: skip + go to finalization step for manual review
        await enrichmentService.skipSession(existingSessionConflict.sessionId);
        const skippedSession = await enrichmentService.getSession(
          existingSessionConflict.sessionId,
        );
        setSession(skippedSession);
        setExistingSessionConflict(null);
        setFlowStep('finalization');
        setFinalizeMessage('Enrichment skipped. You can finalize your profile to proceed.');
      }
    } catch (error: any) {
      console.error('Failed to skip session:', error);
      setSessionError(error.message || 'Failed to skip session.');
    } finally {
      setStartingSession(false);
    }
  };

  const handleCancelExistingSession = async () => {
    if (!existingSessionConflict?.sessionId || existingSessionConflict.sessionId === 'unknown')
      return;

    setStartingSession(true);
    try {
      await enrichmentService.deleteSession(existingSessionConflict.sessionId);
      setExistingSessionConflict(null);
      setSessionError(null);
    } catch (error: any) {
      setSessionError(error.message || 'Failed to cancel session.');
    } finally {
      setStartingSession(false);
    }
  };

  const handleViewSessionWhileWaiting = async () => {
    if (!session) return;
    setCheckingStatus(true);
    try {
      const updatedSession = await enrichmentService.getSession(session.session_id);
      setSession(updatedSession);
    } catch (error: any) {
      setSessionError(error.message || 'Failed to check status.');
    } finally {
      setCheckingStatus(false);
    }
  };

  const handleLoadForm = async () => {
    if (!session) return;
    // Always fetch fresh status — in-memory state can be stale.
    const fresh = await enrichmentService.getSession(session.session_id).catch(() => session);
    if (fresh.status === 'completed') {
      if (features.markEnrichmentCompleteOnFinalize) {
        await markEnrichmentComplete();
      }
      await opts.onAfterFinalize({ session: fresh, company });
      return;
    }

    setLoadingForm(true);
    try {
      const form = await enrichmentService.getForm(session.session_id);
      setFormData(form);

      const initialResponses: Record<string, string> = {};
      form.sections.forEach((section) => {
        section.fields.forEach((field) => {
          if (field.current_value) {
            initialResponses[field.field_id] = field.current_value;
          }
        });
      });
      setFieldResponses(initialResponses);

      setFlowStep('form');
    } catch (error: any) {
      setSessionError(error.message || 'Failed to load form.');
    } finally {
      setLoadingForm(false);
    }
  };

  const handleSkipEnrichment = async () => {
    if (!session) return;
    setCheckingStatus(true);

    try {
      if (features.completeAndExitOnSkip) {
        // Partner: complete + redirect path
        const fresh = await enrichmentService
          .getSession(session.session_id)
          .catch(() => session);
        if (fresh.status === 'completed') {
          if (features.markEnrichmentCompleteOnFinalize) {
            await markEnrichmentComplete();
          }
          await opts.onAfterSkipToCompletion({ sessionId: session.session_id });
          return;
        }

        if (features.writeLocalEnrichmentStatusOnSkip && typeof window !== 'undefined') {
          localStorage.setItem('enrichmentStatus', 'completed');
        }
        // skipSession does a partial finalize on the backend
        await enrichmentService.skipSession(session.session_id);
        if (features.markEnrichmentCompleteOnFinalize) {
          await markEnrichmentComplete();
        }
        await opts.onAfterSkipToCompletion({ sessionId: session.session_id });
      } else {
        // Admin: skip + go to finalization step
        await enrichmentService.skipSession(session.session_id);
        const skippedSession = await enrichmentService.getSession(session.session_id);
        setSession(skippedSession);
        setFlowStep('finalization');
        setFinalizeMessage('Enrichment skipped. You can finalize your profile to proceed.');
      }
    } catch (error: any) {
      console.error('Failed to skip enrichment:', error);
      setSessionError(error.message || 'Failed to skip enrichment.');
    } finally {
      setCheckingStatus(false);
    }
  };

  const handleCancelFromProcessing = async () => {
    if (!session) return;
    setCheckingStatus(true);
    try {
      await enrichmentService.deleteSession(session.session_id);
      setSession(null);
      setFlowStep('upload');
      setSessionError(null);
    } catch (error: any) {
      setSessionError(error.message || 'Unable to cancel session.');
    } finally {
      setCheckingStatus(false);
    }
  };

  const handleFieldChange = (field: EnrichmentField, value: string) => {
    setFieldResponses((prev) => ({ ...prev, [field.field_id]: value }));
  };

  const handleAdditionalUploadComplete = (documentId: number) => {
    setAdditionalDocumentIds((prev) => [...prev, documentId]);
  };

  const handleSubmitResponses = async () => {
    if (!session || !formData) return;
    setResponseMessage(null);
    setSessionError(null);

    const activeFieldIds = new Set(
      formData.sections.flatMap((section) =>
        section.fields.filter((f) => !f.locked).map((f) => f.field_id),
      ) || [],
    );

    const payloadEntries = Array.from(activeFieldIds).map((fieldId) => [
      fieldId,
      fieldResponses[fieldId]?.trim() ?? '',
    ]);
    const payloadObject = Object.fromEntries(
      payloadEntries.filter(([, value]) => value !== ''),
    );

    if (Object.keys(payloadObject).length === 0 && additionalDocumentIds.length === 0) {
      setSessionError('Please fill in at least one field or attach documents.');
      return;
    }

    setProcessingAfterSubmit(true);
    setFlowStep('processing');
    setProcessingMessage('Saving your responses...');
    setEnrichmentCompleted(false);
    setSubmittingResponses(true);

    try {
      const result: FieldSubmissionResult = await enrichmentService.submitResponses(
        session.session_id,
        {
          field_responses: payloadObject,
          additional_document_ids: additionalDocumentIds,
        },
      );

      setResponseMessage(result.message || 'Responses submitted successfully.');
      setAdditionalDocumentIds([]);

      if (result.next_action === 'processing') {
        // Async re-scoring dispatched — poll exactly like the initial enrichment.
        // onPoll wires setSession so the progress circle and step indicators update live.
        setProcessingMessage('Re-scoring your responses against the question bank...');
        const readySession = await enrichmentService.pollSessionUntilReady(
          session.session_id,
          3000,
          120,
          (polledSession) => {
            setSession(polledSession);
            setProcessingMessage('Still re-scoring — analysing your responses...');
          },
        );

        if (readySession.status === 'failed') {
          throw new Error('Re-scoring failed. Please try again.');
        }

        setSession(readySession);
        await handlePostRescoringResult(readySession);
      } else if (result.next_action === 'finalize') {
        // Backend reported max tries or score already above threshold — finalize directly
        const updatedSession = await enrichmentService.getSession(session.session_id);
        setSession(updatedSession);
        await handlePostRescoringResult(updatedSession);
      } else {
        // Fallback: fetch fresh session and decide
        const updatedSession = await enrichmentService.getSession(session.session_id);
        setSession(updatedSession);
        await handlePostRescoringResult(updatedSession);
      }
    } catch (error: any) {
      console.error('Failed to submit responses:', error);
      setSessionError(error.message || 'Unable to submit responses.');
      setFlowStep('form');
      setProcessingAfterSubmit(false);
    } finally {
      setSubmittingResponses(false);
    }
  };

  const handleFinalize = async () => {
    if (!session) return;
    setFinalizing(true);
    setFinalizeMessage(null);

    try {
      await enrichmentService.finalizeSession(session.session_id);
      if (features.markEnrichmentCompleteOnFinalize) {
        await markEnrichmentComplete();
      }
      if (opts.onAuthStateMayHaveChanged) {
        await opts.onAuthStateMayHaveChanged();
      }

      setTimeout(() => {
        void opts.onAfterFinalize({ session, company });
      }, 500);
    } catch (error: any) {
      setFinalizeMessage(error.message || 'Failed to finalize session.');
      setFinalizing(false);
    }
  };

  const handleSkipToDashboardFromProcessing = async () => {
    if (!session) {
      if (features.markEnrichmentCompleteOnFinalize) {
        await markEnrichmentComplete().catch(() => {});
      }
      await opts.onAfterSkipToCompletion({ sessionId: null });
      return;
    }

    if (features.completeAndExitOnSkip) {
      // Partner path: same flow as handleSkipEnrichment
      const fresh = await enrichmentService.getSession(session.session_id).catch(() => session);
      if (fresh.status === 'completed') {
        if (features.markEnrichmentCompleteOnFinalize) {
          await markEnrichmentComplete().catch(() => {});
        }
        await opts.onAfterSkipToCompletion({ sessionId: session.session_id });
        return;
      }
      try {
        if (features.writeLocalEnrichmentStatusOnSkip && typeof window !== 'undefined') {
          localStorage.setItem('enrichmentStatus', 'completed');
        }
        await enrichmentService.skipSession(session.session_id);
        if (features.markEnrichmentCompleteOnFinalize) {
          await markEnrichmentComplete().catch(() => {});
        }
        await opts.onAfterSkipToCompletion({ sessionId: session.session_id });
      } catch (error: any) {
        console.error('Failed to skip and finalize:', error);
        setSessionError(error.message || 'Failed to skip enrichment.');
      }
    } else {
      // Admin path: skip session then navigate (consumer's onAfterSkipToCompletion does the nav)
      try {
        await enrichmentService.skipSession(session.session_id);
        await opts.onAfterSkipToCompletion({ sessionId: session.session_id });
      } catch (error: any) {
        console.error('Failed to skip:', error);
        setSessionError(error.message || 'Failed to skip enrichment.');
      }
    }
  };

  const closeConflictModal = () => setExistingSessionConflict(null);

  // ── Progress steps memo ──
  const progressSteps: ProgressStep[] = useMemo(
    () => [
      {
        id: 'onboarding',
        title: 'Company Information',
        description: 'Fill in your company details',
        completed: companySaved,
        active: flowStep === 'onboarding',
      },
      {
        id: 'upload',
        title: 'Upload Documents',
        description: 'Upload your company portfolio',
        completed:
          !!session ||
          ((selectedExistingDocumentIds.length > 0 || initialDocumentIds.length > 0) &&
            flowStep !== 'upload'),
        active: flowStep === 'upload',
      },
      {
        id: 'processing',
        title: 'Processing',
        description: 'Analyzing your documents',
        completed: flowStep === 'form' || flowStep === 'finalization' || flowStep === 'completed',
        active: flowStep === 'processing',
      },
      {
        id: 'form',
        title: 'Review & Complete',
        description: 'Review and fill missing information',
        completed: flowStep === 'finalization' || flowStep === 'completed',
        active: flowStep === 'form',
      },
      {
        id: 'finalization',
        title: 'Finalize',
        description: 'Review and finalize your profile',
        completed: flowStep === 'completed',
        active: flowStep === 'finalization',
      },
    ],
    [flowStep, companySaved, session, selectedExistingDocumentIds, initialDocumentIds],
  );

  return {
    flowStep,
    company,
    companyForm,
    companyLoading,
    companySaved,
    companyError,
    savingCompany,
    existingDocuments,
    selectedExistingDocumentIds,
    initialDocumentIds,
    loadingDocuments,
    session,
    sessionError,
    startingSession,
    existingSessionConflict,
    formData,
    fieldResponses,
    additionalDocumentIds,
    submittingResponses,
    responseMessage,
    finalizing,
    finalizeMessage,
    processingMessage,
    checkingStatus,
    loadingForm,
    enrichmentCompleted,
    processingAfterSubmit,

    readyToStart,
    progressSteps,

    setFlowStep,
    handleCompanyInputChange,
    handleSectorChange,
    handleSaveCompany,
    handleToggleExistingDocument,
    handleInitialUploadComplete,
    handleStartEnrichment,
    handleViewExistingSession,
    handleSkipExistingSession,
    handleCancelExistingSession,
    handleViewSessionWhileWaiting,
    handleLoadForm,
    handleSkipEnrichment,
    handleCancelFromProcessing,
    handleFieldChange,
    handleAdditionalUploadComplete,
    handleSubmitResponses,
    handleFinalize,
    handleSkipToDashboardFromProcessing,
    closeConflictModal,
  };
}
